"use client";

import { ClipboardTextIcon } from "@phosphor-icons/react/dist/ssr";
import Link from "next/link";
import { useTranslations } from "next-intl";

import AssetWrapper from "@/components/ui/assetWrapper";
import EmptyStateMessage from "@/components/ui/emptyStateMessage";
import { cn } from "@/lib/utils";
import type { RecentAviItem } from "@/types";

type RecentAviProps = {
  items: RecentAviItem[] | undefined;
};

export function RecentAviTable({ items }: RecentAviProps) {
  const t = useTranslations("home");
  const isEmpty = !items || items.length === 0;

  return (
    <div
      className={cn(
        "flex w-full flex-1 flex-col",
        isEmpty ? "h-35.75 items-center justify-center" : "items-start gap-2"
      )}
    >
      {isEmpty ? (
        <div className="flex items-center justify-center">
          <EmptyStateMessage
            message={t("empty.recentAvi")}
            icon={<ClipboardTextIcon size={24} className="color-neutral-950" />}
            compact
          />
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="flex size-6 shrink-0 flex-row items-center justify-center gap-[5.54px] rounded-lg bg-[#D5F2F0] p-0">
              <ClipboardTextIcon size={14} className="text-[#1A1A1A]" />
            </div>
            <span className="heading-6 translate-y-0.5 text-neutral-950">
              {t("section.recentAvi")}
            </span>
          </div>

          {/* Card */}
          <div className="w-full">
            <table className="w-full border-collapse">
              <tbody className="flex flex-col gap-1 [&>tr:nth-child(n+4)]:hidden 2xl:[&>tr:nth-child(n+4)]:grid">
                {items?.map((item, index) => (
                  <tr
                    key={item.avi_id + index}
                    className="group relative grid grid-cols-[20px_minmax(0,auto)_minmax(0,auto)_minmax(0,auto)_minmax(0,auto)_minmax(0,1fr)] items-center gap-3 rounded-md border-b border-neutral-100 bg-neutral-50 px-2 py-3 transition-colors hover:bg-neutral-100 2xl:py-4"
                  >
                    {/* Coluna 1: Pillar */}
                    <td className="flex items-center">
                      <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-100 group-hover:bg-white">
                        <span
                          className="font-medium text-neutral-500"
                          style={{
                            fontSize: "clamp(0.625rem, 0.8vw, 0.75rem)",
                            lineHeight: "normal",
                          }}
                        >
                          {item.pillar ? item.pillar.charAt(0).toUpperCase() : "—"}
                        </span>
                      </div>
                      <Link
                        href={`/assessment?paa_uuid=${item.paa_uuid}`}
                        aria-label={t("aviCard.open")}
                        className="focus-visible:ring-ring absolute inset-0 rounded-md focus-visible:ring-2 focus-visible:outline-none"
                      />
                    </td>

                    {/* Coluna 2: AVI Code */}
                    <td className="flex min-w-0 items-center">
                      <span
                        className="block translate-y-px truncate font-medium tracking-[-0.48px] text-neutral-950"
                        style={{
                          fontSize: "clamp(0.625rem, 0.8vw, 0.75rem)",
                          lineHeight: "normal",
                        }}
                      >
                        {item.avi_code}
                      </span>
                    </td>

                    {/* Coluna 3: Project Code */}
                    <td className="flex min-w-0 items-center">
                      <span
                        className="block translate-y-px truncate font-medium tracking-[-0.48px] text-neutral-950"
                        style={{
                          fontSize: "clamp(0.625rem, 0.8vw, 0.75rem)",
                          lineHeight: "normal",
                        }}
                      >
                        {item.project_code}
                      </span>
                    </td>

                    {/* Coluna 4: Standard Acronym */}
                    <td className="flex min-w-0 items-center">
                      <span
                        className="block translate-y-px truncate font-medium tracking-[-0.48px] text-neutral-950"
                        style={{
                          fontSize: "clamp(0.625rem, 0.8vw, 0.75rem)",
                          lineHeight: "normal",
                        }}
                      >
                        {item.standard_acronym}
                      </span>
                    </td>

                    {/* Coluna 5: Site Name */}
                    <td className="flex min-w-0 items-center">
                      <span
                        className="block translate-y-px truncate font-medium tracking-[-0.48px] text-neutral-950"
                        style={{
                          fontSize: "clamp(0.625rem, 0.8vw, 0.75rem)",
                          lineHeight: "normal",
                        }}
                      >
                        {item.site_name}
                      </span>
                    </td>

                    {/* Coluna 6: Assets */}

                    <td className="flex min-w-0 items-center [&_button]:relative [&_button]:z-10">
                      <AssetWrapper assets={item.assets} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
